import { createSelector } from 'reselect';
import { getMachine } from './getMachine';
import { getIsMESEnabled } from './getIsMESEnabled';
import { getMachineSettings } from './getMachineSettings';

// TODO: resource.machineRef is being deprecated. The { resource: { machineRef } } branch below
// covers labor tickets whose machineRef is null and fall back to the linked resource's machineRef.
// Once labor ticket.machineRef is always populated, this fallback can be dropped.
/** @param {number|string} machineRef */
function whereMachineRefOrLegacyResourceLink(machineRef) {
  return {
    _or: [
      { machineRef: { _eq: machineRef } },
      {
        _and: [
          { machineRef: { _is_null: true } },
          { resource: { machineRef: { _eq: machineRef } } },
        ],
      },
    ],
  };
}

/** @param {number|string} machineRef @param {string} laborResourceId */
function whereMachineRefOrLaborResourceLink(machineRef, laborResourceId) {
  return {
    _or: [
      { machineRef: { _eq: machineRef } },
      {
        _and: [
          { machineRef: { _is_null: true } },
          { resourceId: { _eq: laborResourceId } },
        ],
      },
    ],
  };
}

// The current-ticket queries sort tickets without a clockOut first as open.
// A CLOSED ticket missing its clockOut (a sync inconsistency) would then
// outrank the real open ticket, only for getCurrentLaborTicket to hide it.
const NOT_CLOSED_WITHOUT_CLOCK_OUT = {
  _not: {
    _and: [{ state: { _eq: 'CLOSED' } }, { clockOut: { _is_null: true } }],
  },
};

/** @param {object} machineWhere */
function withoutClosedTicketsMissingClockOut(machineWhere) {
  return { _and: [machineWhere, NOT_CLOSED_WITHOUT_CLOCK_OUT] };
}

/**
 * Where clause for the machine's current labor ticket queries
 * (SUBSCRIBE_LABOR_TICKETS, GET_CURRENT_MACHINE_LABOR_TICKET).
 */
export const getLaborTicketWhereForMachine = createSelector(
  [getMachine, getIsMESEnabled, getMachineSettings],
  (machine, isMesEnabled, machineSettings) => {
    const { machineRef } = machine;
    const { laborResourceId } = machineSettings || {};

    if (!isMesEnabled) {
      return withoutClosedTicketsMissingClockOut(
        whereMachineRefOrLegacyResourceLink(machineRef)
      );
    }

    if (laborResourceId != null && laborResourceId !== '') {
      return withoutClosedTicketsMissingClockOut(
        whereMachineRefOrLaborResourceLink(machineRef, laborResourceId)
      );
    }

    return withoutClosedTicketsMissingClockOut(
      whereMachineRefOrLegacyResourceLink(machineRef)
    );
  }
);
